import React, {useState} from 'react';
import {SponsorshipTier, SponsorshipTierData} from "types/SponsorshipTier";
import {Form, Formik, FormikHelpers} from "formik";
import Label from "components/Form/Label";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import FieldHeader from "components/Form/FieldHeader";
import {Tab} from "@headlessui/react";
import TabButton from "components/TabButton";
import {ExtendedDurationPicker} from "components/Form/DurationPicker";
import MoneyInput from "components/Form/MoneyInput";
import Uploader from "components/Form/Uploader";
import {PhotographIcon} from "@heroicons/react/outline";
import {useOperator} from "context/OperatorContext";
import {DurationUnit} from "../../../components/Form/DurationPicker/ExtendedDurationPicker";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {TextAreaField} from "../../../components/Form/Form";

enum PriceMode {FREE, CHARGE}

type Props = {
    sponsorshipTier?: SponsorshipTier
    onSubmit: (values: SponsorshipTierData, helpers: FormikHelpers<SponsorshipTierData>) => any
}

function SponsorshipTierForm(props: Props) {
    const {termFor} = useOperator()

    const [priceMode, setPriceMode] = useState<PriceMode>(props.sponsorshipTier?.price ? PriceMode.CHARGE : PriceMode.FREE);
    const [hasCustomSymbol, setHasCustomSymbol] = useState<boolean>(!!props.sponsorshipTier?.symbol);

    const selectPriceMode = (mode: PriceMode, {setFieldValue}: FormikHelpers<SponsorshipTierData>) => {
        setPriceMode(mode)
        void setFieldValue('price', mode === PriceMode.FREE ? 0 : (props.sponsorshipTier?.price || 1000))
    }

    const selectAppearanceMode = (hasCustomSymbol: boolean) => {
        setHasCustomSymbol(hasCustomSymbol)
    }

    const validationSchema = Yup.object().shape({
      name: Yup.string().required("This field is required").max(255, "Maximum character limit exceeded"),
      description: Yup.string().max(255, "Maximum character limit exceeded"),
      rank: Yup.string().required("This field is required"),
      duration: Yup.string().notOneOf(["PT0S"], "This field is required").required("This field is required"),
    })

    const initialValues: SponsorshipTierData = {
        name: props.sponsorshipTier?.name || '',
        description: props.sponsorshipTier?.description || '',
        duration: props.sponsorshipTier?.duration.toISO() || 'P1Y',
        price: props.sponsorshipTier?.price || 0,
        symbol_id: props.sponsorshipTier?.symbol?.id || 0,
        colour: props.sponsorshipTier?.colour || '',
        rank: props.sponsorshipTier?.rank ?? 1,
    }

    const submit = (values: SponsorshipTierData, helpers: FormikHelpers<SponsorshipTierData>) => {
        if(hasCustomSymbol) {
            values.colour = null
        } else {
            values.symbol_id = null
        }
        props.onSubmit(values, helpers)
    }

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={submit}>
            {formik => (
                <Form className="space-y-8 divide-y divide-gray-200">
                  <ErrorNotification formik={formik} />

                    <div className="shadow sm:rounded-md">
                        <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                            <div className="space-y-8 divide-y divide-gray-200">
                                <div>
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                                            Key Information
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Basic details about the {termFor('sponsorship')} tier
                                        </p>
                                    </div>
                                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                                        {/* Name */}
                                        <div className="sm:col-span-3">
                                            <Field type="text"
                                                   name="name"
                                                   id="name"
                                                   label="Name *"
                                                   autoComplete="off"
                                                   maxLength="255"
                                                   autoFocus
                                            />
                                        </div>

                                        {/* Rank */}
                                        <div className="sm:col-span-2">
                                            <Field type="number"
                                                   name="rank"
                                                   id="rank"
                                                   label="Rank *"
                                                   autoComplete="off"
                                                   min={0}
                                                   max={255}
                                                   step={1}
                                            />
                                        </div>

                                        {/* Description */}
                                        <div className="sm:col-span-5">
                                            <TextAreaField
                                              name="description"
                                              label="Description"
                                              hint="Optional. 255 chars max."
                                              id="description"
                                              autoComplete="off"
                                              maxChars={255}
                                              rows={3}
                                            />
                                        </div>

                                        {/* Duration */}
                                        <div className="sm:col-span-6 sm:col-start-1">
                                            <Label htmlFor="duration">Duration *</Label>
                                            <div className="mt-1">
                                                <ExtendedDurationPicker
                                                    units={[DurationUnit.YEAR, DurationUnit.MONTH, DurationUnit.WEEK, DurationUnit.DAY]}
                                                    value={formik.values.duration}
                                                    onChange={duration => formik.setFieldValue('duration', duration.toISO())}
                                                />

                                                {(!!formik.errors?.duration && !!formik.touched?.duration) &&
                                                  <FieldHelp isError>
                                                    {formik.errors.duration}
                                                  </FieldHelp>
                                                }
                                            </div>
                                        </div>

                                        {/* Price */}
                                        <div
                                            className="sm:col-span-3 sm:col-start-1 md:col-span-2 md:col-start-1 xl:col-span-1 xl:col-start-1">
                                            <FieldHeader label="Price *"/>
                                            <Tab.Group selectedIndex={priceMode}
                                                       onChange={index => selectPriceMode(index, formik)}>
                                                <Tab.List
                                                    className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                                                    <TabButton position={TabButton.positions.FIRST}>Free</TabButton>
                                                    <TabButton position={TabButton.positions.LAST}>Charge</TabButton>
                                                </Tab.List>
                                                <Tab.Panels>
                                                    <Tab.Panel/>
                                                    <Tab.Panel>
                                                        <MoneyInput
                                                            currency={MoneyInput.Currency.GBP}
                                                            value={formik.values.price}
                                                            onChange={amount => formik.setFieldValue('price', amount)}
                                                            required
                                                        />
                                                    </Tab.Panel>
                                                </Tab.Panels>
                                            </Tab.Group>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-8">
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                                            Appearance
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Configure the appearance of sponsorships in this tier
                                        </p>
                                    </div>
                                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                                        <div className="sm:col-span-6">
                                            <FieldHeader label="Symbol"/>
                                            <Tab.Group selectedIndex={Number(hasCustomSymbol)}
                                                       onChange={index => selectAppearanceMode(!!index)}>
                                                <Tab.List
                                                    className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                                                    <TabButton position={TabButton.positions.FIRST}>Standard</TabButton>
                                                    <TabButton position={TabButton.positions.LAST}>Custom</TabButton>
                                                </Tab.List>
                                                <Tab.Panels className="mt-6">
                                                    <Tab.Panel
                                                        className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                                        {/* Colour */}
                                                        <div className="sm:col-span-1">
                                                            <FieldHeader label="Colour"/>
                                                            <Field
                                                                className="appearance-none w-full h-14 -ml-0.5 bg-transparent border-0 cursor-pointer"
                                                                type="color"
                                                                name="colour"
                                                                id="colour"
                                                                required
                                                            />
                                                        </div>
                                                    </Tab.Panel>
                                                    <Tab.Panel>
                                                        {/* Image */}
                                                        <FieldHeader label="Image"/>
                                                        <Uploader
                                                            icon={PhotographIcon}
                                                            files={props.sponsorshipTier?.symbol}
                                                            type={Uploader.types.IMAGE}
                                                            minFiles={1}
                                                            maxFiles={1}
                                                            onChange={files => formik.setFieldValue('symbol_id', files[0] ? files[0].id : null)}
                                                        />
                                                    </Tab.Panel>
                                                </Tab.Panels>
                                            </Tab.Group>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>

                        <FormControls formik={formik}/>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export default SponsorshipTierForm;